import { songs, getPlayableSongs } from "./songs.js"
import Hls from 'hls.js';
import { socket, userName as initialUserName, deviceId } from "./socket.js";
import { getDisplayName, isLoggedIn, openAuthModal, openProfileModal, initAuthModal, initProfileModal, onAuthChange, authFetch, openUserCard, initUserCardModal, getToken, getBackendUser, waitForAuth } from "./auth.js";
import { modalOpen, modalClose } from './modal-state.js';
import { DATE_LOCALE } from './date-locale.js';
import { setActiveRoomForAdmin, clearActiveRoomForAdmin } from './room-admin.js';
import { setActiveRoomForMessageAdmin, clearActiveRoomForMessageAdmin, markMessageElementForAdmin } from './message-admin.js';

const apiUrl = import.meta.env.VITE_API_BASE_URL;

let userName = getDisplayName();
let currentStream = null;
let usingSecondaryStream = false;
let leaderboardLoaded = false;

// ####### LOAD LIVESTREAM #######

// LL-HLS STREAM:
const video = document.getElementById('cat-livestream');
const loadingMessage = document.getElementById('livestream-loading-message');
const archivedMessage = document.getElementById('livestream-archived-message');

// Set video attributes for autoplay
video.muted = true;
video.autoplay = true;
video.playsInline = true;

let hls = null;
let streamReconnectTimeout = null;
let lastUnfocusedAt = null;
const UNFOCUSED_RELOAD_THRESHOLD_MS = 35_000; // 35 seconds
const VOLUME_ICON_MUTED = '/icons/volume-muted.svg';
const VOLUME_ICON_LOW = '/icons/volume-low.svg';
const VOLUME_ICON_HIGH = '/icons/volume-high.svg';
const DEFAULT_VOLUME = 60;
const FEEDING_DISABLED_MESSAGE = '[feeding disabled for now]';
const FEEDING_ARCHIVED_MESSAGE = '[room archived]';
const DONATION_POPUP_DELAY_SECONDS = 80;
const DONATION_POPUP_COOKIE_NAME = 'catfeeder_donation_popup_seen';
const DONATION_POPUP_COOKIE_MAX_AGE_DAYS = 7;
let lastSetVolume = DEFAULT_VOLUME;
let autoPlayInitialized = false;
let musicInitialized = false;
let musicControlsBound = false;
let musicHeaderResizeBound = false;
let radioVisibilitySyncBound = false;
let radioUnlockedByUser = false;
let feedStatusInterval = null;
let chatFormSubmitHandler = null;
let chatInputResizeHandler = null;
let rateLimitInterval = null;
let donationPopupTimeout = null;
let dailyFeedLimitInterval = null;

function canFeedInCurrentRoom() {
  if (currentStream?.archived) return false;
  const permission = currentStream?.feeding_permission;
  if (!permission || permission === 'DISABLED') return false;
  if (permission === 'ANYONE') return true;
  if (permission === 'AUTH_USERS') return true;
  if (permission === 'ADMINS') return currentStream?.user_is_admin === true;
  return false;
}

function isFeedingDisabledEntirely() {
  const permission = currentStream?.feeding_permission;
  return currentStream?.archived || permission === 'DISABLED';
}

function updateFeedButtonsDisabled(disabled, countdownText = '', disabledMessage = '') {
  const feedButtons = document.querySelectorAll('.feed-button');
  feedButtons.forEach((button) => {
    if (!button.dataset.roomId) return;
    if (!currentStream?.name) return;
    if (button.dataset.roomId !== currentStream.name) return;

    button.disabled = disabled;
    if (disabled) {
      if (disabledMessage) {
        button.textContent = currentStream.archived ? FEEDING_ARCHIVED_MESSAGE : disabledMessage;
      } else if (countdownText) {
        button.textContent = `[feeding available in ${countdownText}]`;
      }
    } else {
      button.textContent = '[feed the cat]';
    }
    fitButtonText(button);
  });
}

function fitButtonText(button) {
  if (!button || button.offsetWidth === 0) return;
  
  // Reset to base size to measure
  button.style.fontSize = '1rem';
  
  // If it overflows, shrink it
  let size = 16; // 1rem = 16px
  const minSize = 10;
  
  while (button.scrollWidth > button.clientWidth && size > minSize) {
    size -= 0.5;
    button.style.fontSize = `${size}px`;
  }
}

function showLoading() {
  if (loadingMessage) loadingMessage.style.display = 'flex';
}

function hideLoading() {
  if (loadingMessage) loadingMessage.style.display = 'none';
}

function initializeStream(streamUrl) {
  // Clear any existing timeout
  if (streamReconnectTimeout) {
    clearTimeout(streamReconnectTimeout);
    streamReconnectTimeout = null;
  }

  // Destroy existing HLS instance if it exists
  if (hls) {
    hls.destroy();
    hls = null;
  }

  // Show loading while we (re)initialize
  showLoading();

  if (Hls.isSupported()) {
    hls = new Hls({
      lowLatencyMode: true,
      liveSyncDurationCount: 2,      // stay near the live edge
      liveMaxLatencyDurationCount: 3,
      maxLiveSyncPlaybackRate: 2,  // speed up x2 to catch up
      backBufferLength: 90,
    });
    hls.loadSource(streamUrl);
    hls.attachMedia(video);

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      video.play().catch((err) => {
        console.warn('Autoplay prevented');
        console.error(err);
      });
    });

    // Hide loading once the video is actually playing
    const onPlayingOnce = () => {
      hideLoading();
      video.removeEventListener('playing', onPlayingOnce);
    };
    video.addEventListener('playing', onPlayingOnce);
  } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
    // For Safari
    // Show loading until Safari starts playing
    const onSafariCanPlay = () => {
      // wait for real playback start if possible
    };
    const onSafariPlaying = () => {
      hideLoading();
      video.removeEventListener('canplay', onSafariCanPlay);
      video.removeEventListener('playing', onSafariPlaying);
    };
    video.addEventListener('canplay', onSafariCanPlay);
    video.addEventListener('playing', onSafariPlaying);

    video.src = streamUrl;
    video.addEventListener('loadedmetadata', () => {
      video.play().catch((err) => {
        console.warn('Autoplay prevented on Safari');
        console.error(err);
      });
    });
  } else {
    console.error('This browser does not support HLS playback');
    // TODO: WebRTC fallback
  }
}

function stopStream() {
  if (hls) {
    try {
      hls.detachMedia();
      hls.destroy();
    } catch (_) {}
    hls = null;
  }

  if (video) {
    try {
      video.pause();
    } catch (_) {}
    video.removeAttribute('src');
    video.removeAttribute('poster');
    video.load();
  }
}

function getActiveStreamUrl() {
  if (!currentStream || currentStream.archived) return null;
  if (usingSecondaryStream && currentStream.secondary_stream_url) {
    return currentStream.secondary_stream_url;
  }
  return currentStream.stream_url;
}

function reconnectStream() {
  const url = getActiveStreamUrl();
  if (url) {
    initializeStream(url);
  }
}

function swapCamera() {
  if (!currentStream?.secondary_stream_url) return;
  usingSecondaryStream = !usingSecondaryStream;
  const url = getActiveStreamUrl();
  if (url) {
    initializeStream(url);
  }
}

// Handle visibility change - when user returns to tab
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) {
    const wasAwayMs = lastUnfocusedAt ? Date.now() - lastUnfocusedAt : 0;
    if (wasAwayMs >= UNFOCUSED_RELOAD_THRESHOLD_MS && (video.paused || video.readyState < 3)) {
      streamReconnectTimeout = setTimeout(reconnectStream, 500);
    }
  } else {
    lastUnfocusedAt = Date.now();
  }
});

// Handle page focus - additional mobile support
window.addEventListener('focus', () => {
  const wasAwayMs = lastUnfocusedAt ? Date.now() - lastUnfocusedAt : 0;
  if (wasAwayMs >= UNFOCUSED_RELOAD_THRESHOLD_MS && (video.paused || video.readyState < 3)) {
    streamReconnectTimeout = setTimeout(reconnectStream, 500);
  }
});

window.addEventListener('blur', () => {
  lastUnfocusedAt = Date.now();
});

// Socket functionality
function removeSocketListeners() {
  try {
    socket.off("new_message");
    socket.off("number_online_users");
    socket.off("error");
    socket.off("cat_fed");
  } catch (_) {}
}

// A reconnected socket has a new id that is in no room, so without this the tab silently stops
// getting chat, feeds and the viewer count, e.g. after every backend restart.
// The manager's 'reconnect' only fires on reconnections; setupSocket joins on the first connect.
socket.io.on('reconnect', () => {
  if (!currentStream) return;
  socket.emit('join_room', { roomId: currentStream.name });
  // Anything posted while disconnected never reached this tab
  fetchChatHistory(currentStream.name);
});

function showChatLoadingSkeleton(chatMessages) {
  if (!chatMessages) return;
  chatMessages.dataset.loading = 'true';
  chatMessages.dataset.lastDate = '';
  chatMessages.innerHTML = '';

  const approxRowHeight = 52;
  const targetHeight = chatMessages.clientHeight || 520;
  const skeletonCount = Math.max(8, Math.ceil(targetHeight / approxRowHeight));
  const fragment = document.createDocumentFragment();

  for (let i = 0; i < skeletonCount; i += 1) {
    const skeleton = document.createElement('div');
    skeleton.className = 'chat-message-skeleton';

    const meta = document.createElement('div');
    meta.className = 'chat-message-skeleton__meta';

    const lineShort = document.createElement('div');
    lineShort.className = 'chat-message-skeleton__line chat-message-skeleton__line--short';

    const lineFull = document.createElement('div');
    lineFull.className = 'chat-message-skeleton__line';

    skeleton.appendChild(meta);
    skeleton.appendChild(lineShort);
    skeleton.appendChild(lineFull);
    fragment.appendChild(skeleton);
  }

  chatMessages.appendChild(fragment);
}

function clearChatLoadingState(chatMessages, { preserveDate = false } = {}) {
  if (!chatMessages) return;
  if (chatMessages.dataset.loading === 'true') {
    chatMessages.innerHTML = '';
    if (!preserveDate) {
      chatMessages.dataset.lastDate = '';
    }
  }
  chatMessages.dataset.loading = 'false';
}

function getRoomRoles(roomAdminUsers, roomName) {
  if (!roomName || !Array.isArray(roomAdminUsers)) return null;
  const entry = roomAdminUsers.find((e) => e?.room_name === roomName);
  return entry?.roles && Array.isArray(entry.roles) ? entry.roles : null;
}

function createChatAdminCrown() {
  const crown = document.createElement('span');
  crown.className = 'chat-admin-crown';
  crown.title = 'Room admin';
  const img = document.createElement('img');
  img.className = 'chat-admin-crown-icon';
  img.src = '/icons/crown.svg';
  img.alt = '';
  img.setAttribute('aria-hidden', 'true');
  crown.appendChild(img);
  return crown;
}

// The message types appendChatMessage knows how to render
const RENDERED_MESSAGE_TYPES = new Set(['USER', 'CAT_FED', 'JOINED']);

function appendChatMessage(container, message) {
  if (!container) return;
  // Skip types this version doesn't render (e.g. ones the backend adds later), which would
  // otherwise show up as an empty line and maybe a stray date separator
  if (!RENDERED_MESSAGE_TYPES.has(message.message_type)) return;
  clearChatLoadingState(container, { preserveDate: true });

  const msgDiv = document.createElement('div');
  msgDiv.className = 'chat-message';
  const createdAt = new Date(message.created_at.replace(' ', 'T') + 'Z');
  const dateString = createdAt.toLocaleDateString(DATE_LOCALE, { month: 'numeric', day: 'numeric', year: 'numeric' });
  const timeString = createdAt.toLocaleTimeString(DATE_LOCALE, { hour: '2-digit', minute: '2-digit' });

  if (container.dataset.lastDate !== dateString) {
    const separator = document.createElement('div');
    separator.className = 'chat-date-separator';
    separator.textContent = dateString;
    container.appendChild(separator);
    container.dataset.lastDate = dateString;
  }

  const messageType = message.message_type;
  const userObj = message.user;
  const hasUserObj = !!(userObj && typeof userObj === 'object' && Object.keys(userObj).length > 0);
  const displayName = hasUserObj ? (userObj.display_name || message.display_name) : message.display_name;
  const userId = hasUserObj ? userObj.id : message.user_id;
  const avatarUrl = hasUserObj ? userObj.avatar_url : null;
  const chatRoles = hasUserObj ? getRoomRoles(userObj.room_admin_users, currentStream?.name) : null;
  const isRoomOwner = chatRoles?.includes('ROOM_OWNER') ?? false;

  if (messageType === 'USER') {
    const timestamp = document.createElement('span');
    timestamp.className = 'chat-timestamp';
    timestamp.textContent = timeString;
    
    const username = document.createElement('span');
    username.className = 'chat-username';
    username.textContent = displayName;

    if (userId) {
      username.classList.add('chat-username--registered');
      username.addEventListener('click', () => {
        const self = getBackendUser();
        if (self && self.id === userId) {
          openProfileModal();
        } else {
          openUserCard(userId);
        }
      });
    }
    
    const content = document.createElement('span');
    content.className = 'chat-content';
    content.textContent = message.content;
    
    msgDiv.appendChild(timestamp);
    if (hasUserObj) {
      if (isRoomOwner) msgDiv.appendChild(createChatAdminCrown());
      const avatar = document.createElement('span');
      avatar.className = 'chat-avatar';
      if (avatarUrl) {
        const img = document.createElement('img');
        img.className = 'chat-avatar-img';
        img.alt = '';
        img.src = avatarUrl;
        const placeholder = document.createElement('img');
        placeholder.src = '/icons/user.svg';
        placeholder.alt = '';
        placeholder.className = 'chat-avatar-placeholder';
        placeholder.style.display = 'none';
        img.onerror = () => { img.style.display = 'none'; placeholder.style.display = ''; };
        avatar.append(img, placeholder);
      } else {
        const placeholder = document.createElement('img');
        placeholder.src = '/icons/user.svg';
        placeholder.alt = '';
        placeholder.className = 'chat-avatar-placeholder';
        avatar.appendChild(placeholder);
      }
      msgDiv.appendChild(avatar);
    }
    msgDiv.appendChild(username);
    msgDiv.appendChild(content);
    markMessageElementForAdmin(msgDiv, message);
  } else if (messageType === 'CAT_FED') {
    msgDiv.className = 'chat-message chat-message-system';
    
    const timestamp = document.createElement('span');
    timestamp.className = 'chat-timestamp';
    timestamp.textContent = timeString;
    
    const username = document.createElement('span');
    username.className = 'chat-fed-username';
    username.textContent = displayName;

    if (userId) {
      username.classList.add('chat-username--registered');
      username.addEventListener('click', () => {
        const self = getBackendUser();
        if (self && self.id === userId) {
          openProfileModal();
        } else {
          openUserCard(userId);
        }
      });
    }

    const content = document.createElement('span');
    content.className = 'chat-content-system';
    content.style.color = '#ffff62db';
    content.textContent = message.content;
    
    msgDiv.appendChild(timestamp);
    if (hasUserObj) {
      if (isRoomOwner) msgDiv.appendChild(createChatAdminCrown());
      const avatar = document.createElement('span');
      avatar.className = 'chat-avatar';
      if (avatarUrl) {
        const img = document.createElement('img');
        img.className = 'chat-avatar-img';
        img.alt = '';
        img.src = avatarUrl;
        const placeholder = document.createElement('img');
        placeholder.src = '/icons/user.svg';
        placeholder.alt = '';
        placeholder.className = 'chat-avatar-placeholder';
        placeholder.style.display = 'none';
        img.onerror = () => { img.style.display = 'none'; placeholder.style.display = ''; };
        avatar.append(img, placeholder);
      } else {
        const placeholder = document.createElement('img');
        placeholder.src = '/icons/user.svg';
        placeholder.alt = '';
        placeholder.className = 'chat-avatar-placeholder';
        avatar.appendChild(placeholder);
      }
      msgDiv.appendChild(avatar);
    }
    msgDiv.appendChild(username);
    msgDiv.appendChild(content);

    if (typeof message.seconds_left_to_feed === 'number') {
      startFeedStatusCountdown(message.seconds_left_to_feed);
    }
  } else if (messageType === 'JOINED') {
    msgDiv.className = 'chat-message chat-message-system';
    
    const timestamp = document.createElement('span');
    timestamp.className = 'chat-timestamp';
    timestamp.textContent = timeString;
    
    const content = document.createElement('span');
    content.className = 'chat-content-system';
    content.style.color = '#ccffcc';
    content.textContent = `${displayName} joined the chat`;
    
    msgDiv.appendChild(timestamp);
    msgDiv.appendChild(content);
  }

  container.appendChild(msgDiv);
}

async function fetchChatHistory(roomId) {
  const chatMessages = document.querySelector('.chat-messages');
  if (!chatMessages) return;

  try {
    const res = await fetch(`${apiUrl}/rooms/${roomId}/chat`);
    if (!res.ok) throw new Error(`Failed to fetch chat: ${res.status}`);
    const data = await res.json();

    // The viewer may have moved to another room while this was loading
    if (currentStream?.name !== roomId) return;

    // A reconnect re-renders a chat the viewer may be reading further up
    const keepScrollPosition = chatMessages.dataset.loading !== 'true' && !isNearBottom(chatMessages);
    const { scrollTop } = chatMessages;

    clearChatLoadingState(chatMessages);
    chatMessages.innerHTML = '';
    chatMessages.dataset.lastDate = '';

    (data.chat?.messages || []).forEach(message => {
      appendChatMessage(chatMessages, message);
    });

    const msgDiv = document.createElement('div');
    msgDiv.className = 'chat-message chat-message-system';
    const span = document.createElement('span');
    span.className = 'chat-content-system';
    span.style.color = '#ccffcc';
    span.textContent = 'you joined.';
    msgDiv.appendChild(span);
    chatMessages.appendChild(msgDiv);

    if (keepScrollPosition) {
      chatMessages.scrollTop = scrollTop;
    } else {
      scrollToBottom(chatMessages);
    }
  } catch (err) {
    console.error('Error fetching chat history:', err);
    clearChatLoadingState(chatMessages);
  }
}

function setupSocket(roomId) {
  if (roomId) {
    socket.emit('join_room', { roomId: roomId });
    import('./router.js').then(({ router }) => {
      router.setCurrentRoom(roomId);
    });
  } else {
    console.error('Missing room id');
  }

  removeSocketListeners();

  socket.on("number_online_users", (data) => {
    const numberOnlineUsers = document.querySelector('.number-online-users');
    numberOnlineUsers.innerText = data.number_online_users;
  });

  socket.on("error", (data) => {
    if (data.error_type === "RATE_LIMIT_EXCEEDED") {
      handleRateLimitError(data.message, data.retry_after);
    } else {
      console.error("Socket error:", data);
    }
  });

  socket.on("cat_fed", (data) => {
    if (typeof data.seconds_left_to_feed === 'number') {
      startFeedStatusCountdown(data.seconds_left_to_feed);
    }
  });

  socket.on("new_message", (data) => {
    const chatMessages = document.querySelector('.chat-messages');
    if (!chatMessages) return;

    const chatNotificationSound = new Audio('/sounds/chat_notification.mp3');
    chatNotificationSound.volume = 0.5;

    const wasAtBottom = isNearBottom(chatMessages);
    appendChatMessage(chatMessages, data);
    if (wasAtBottom) scrollToBottom(chatMessages);

    if (data.message_type === 'USER') {
      chatNotificationSound.play();
    } else if (data.message_type === 'CAT_FED') {
      playFeedNotificationSound();
    }
  });
}

// Whoever feeds gets both the /feed response and the CAT_FED socket message, in either
// order, so a second play inside this window is the same feed. Room cooldowns are >= 60 s.
const FEED_SOUND_DEDUPE_MS = 10000;
let lastFeedSoundAt = 0;
function playFeedNotificationSound() {
  const now = Date.now();
  if (now - lastFeedSoundAt < FEED_SOUND_DEDUPE_MS) return;
  lastFeedSoundAt = now;
  const feedNotificationSound = new Audio('/sounds/feed_notification.mp3');
  feedNotificationSound.volume = 0.5;
  feedNotificationSound.play().catch(() => {});
}

function isNearBottom(element, threshold = 50) {
  if (!element) return false;
  return element.scrollHeight - element.scrollTop - element.clientHeight <= threshold;
}

function scrollToBottom(element) {
  if (element) {
    element.scrollTop = element.scrollHeight;
  }
}

// Feed functionality
let cooldownInterval = 0;
function cooldownMessage(cooldownTime, lastFeedTimeStr) {
  const lastFeedTime = new Date(lastFeedTimeStr.replace(" ", "T") + "Z"); // Ensure proper ISO format
  
  const messageElement = document.querySelector('.cooldown-message');

  if (!messageElement) {
    console.error('Message element not found');
    return;
  }

  // Clear any existing interval before starting a new one
  clearInterval(cooldownInterval);

  messageElement.style.display = 'block';
  messageElement.style.opacity = '1';

  let now = new Date();
  let diffMs = now - lastFeedTime;
  let diffSeconds = Math.max(0, cooldownTime - Math.floor(diffMs / 1000));
  let diffH = Math.floor(diffSeconds / 3600);
  let diffM = Math.floor((diffSeconds % 3600) / 60);
  let diffS = diffSeconds % 60;

  if (diffH > 0) {
    messageElement.innerText = `Error: You need to wait ${diffH} hours, ${diffM} minutes and ${diffS} seconds before feeding the cat again.`;
  } else {
    messageElement.innerText = `Error: You need to wait ${diffM} minutes and ${diffS} seconds before feeding the cat again.`;
  }

  let messageTime = 3; // Time that the message should be displayed (in seconds)

  cooldownInterval = setInterval(() => {
    now = new Date();
    diffMs = now - lastFeedTime;
    diffSeconds = Math.max(0, cooldownTime - Math.floor(diffMs / 1000));
    diffH = Math.floor(diffSeconds / 3600);
    diffM = Math.floor((diffSeconds % 3600) / 60);
    diffS = diffSeconds % 60;

    if (messageTime > 0 && diffSeconds > 0) {
      if (diffH > 0) {
        messageElement.innerText = `Error: You need to wait ${diffH} hours, ${diffM} minutes and ${diffS} seconds before feeding the cat again.`;
      } else {
        messageElement.innerText = `Error: You need to wait ${diffM} minutes and ${diffS} seconds before feeding the cat again.`;
      }
      
      messageTime--;
    } else {
      clearInterval(cooldownInterval);
      messageElement.style.opacity = '0';
      setTimeout(() => {
        messageElement.style.display = 'none';
      }, 1000);
    }
  }, 1000); // Update every second
}

function sleepMessage(cooldownTime, nightScheduleStartHour) {
  let nowDate = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate(), new Date().getUTCHours(), new Date().getUTCMinutes(), new Date().getUTCSeconds()));
  const sleepTime = new Date(Date.UTC(
    nowDate.getUTCFullYear(),
    nowDate.getUTCMonth(), 
    nowDate.getUTCDate(),
    nightScheduleStartHour,
    0,
    0
  ));

  const messageElement = document.querySelector('.cooldown-message');

  if (!messageElement) {
    console.error('Message element not found');
    return;
  }

  // Clear any existing interval before starting a new one
  clearInterval(cooldownInterval);

  messageElement.style.display = 'block';
  messageElement.style.opacity = '1';

  let diffMs = nowDate - sleepTime;
  let diffSeconds = Math.max(0, cooldownTime - Math.floor(diffMs / 1000));
  let diffH = Math.floor(diffSeconds / 3600);
  let diffM = Math.floor((diffSeconds % 3600) / 60);
  let diffS = diffSeconds % 60;

  if (diffH > 0) {
    messageElement.innerText = `Error: the cat is sleeping right now. Try again in ${diffH} hours, ${diffM} minutes and ${diffS} seconds.`;
  } else {
    messageElement.innerText = `Error: the cat is sleeping right now. Try again in ${diffH} hours, ${diffM} minutes and ${diffS} seconds.`;
  }

  let messageTime = 3; // Time that the message should be displayed (in seconds)

  cooldownInterval = setInterval(() => {
    nowDate = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate(), new Date().getUTCHours(), new Date().getUTCMinutes(), new Date().getUTCSeconds()));
    diffMs = nowDate - sleepTime;
    diffSeconds = Math.max(0, cooldownTime - Math.floor(diffMs / 1000));
    diffH = Math.floor(diffSeconds / 3600);
    diffM = Math.floor((diffSeconds % 3600) / 60);
    diffS = diffSeconds % 60;

    if (messageTime > 0 && diffSeconds > 0) {
      if (diffH > 0) {
        messageElement.innerText = `Error: the cat is sleeping right now. Try again in ${diffH} hours, ${diffM} minutes and ${diffS} seconds.`;
      } else {
        messageElement.innerText = `Error: the cat is sleeping right now. Try again in ${diffH} hours, ${diffM} minutes and ${diffS} seconds.`;
      }
      
      messageTime--;
    } else {
      clearInterval(cooldownInterval);
      messageElement.style.opacity = '0';
      setTimeout(() => {
        messageElement.style.display = 'none';
      }, 1000);
    }
  }, 1000); // Update every second
}

function successMessage(message) {
  // Create a message element
  const messageElement = document.querySelector('.success-message');
  messageElement.style.display = 'block';
  messageElement.style.opacity = '1';
  messageElement.innerText = message;

  if (!messageElement) {
    console.error('Message element not found');
    return;
  }
  messageElement.style.display = 'block';

  // Remove the message after 3 seconds
  setTimeout(() => {
    messageElement.style.opacity = '0';
    setTimeout(() => {
      messageElement.style.display = 'none';
    }, 500);
  }, 3500);
}

function pluralize(count, word) {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

// e.g. "5 hours 3 minutes and 2 seconds", leaving out leading zero units
function formatDailyFeedLimitWait(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];
  if (hours > 0) parts.push(pluralize(hours, 'hour'));
  if (hours > 0 || minutes > 0) parts.push(pluralize(minutes, 'minute'));
  parts.push(`${parts.length ? 'and ' : ''}${pluralize(seconds, 'second')}`);
  return parts.join(' ');
}

function closeDailyFeedLimitPopup() {
  const modal = document.getElementById('dailyFeedLimitModal');
  if (!modal?.classList.contains('active')) return;
  clearInterval(dailyFeedLimitInterval);
  dailyFeedLimitInterval = null;
  modal.classList.remove('active');
  modalClose();
}

function showDailyFeedLimitPopup(feedsToday, maxFeedsPerDay, retryAfterSeconds) {
  const modal = document.getElementById('dailyFeedLimitModal');
  const countdownElement = document.getElementById('dailyFeedLimitCountdown');
  if (!modal || !countdownElement) return;

  if (!modal.dataset.bound) {
    modal.dataset.bound = 'true';
    document.getElementById('dailyFeedLimitOk')?.addEventListener('click', closeDailyFeedLimitPopup);
    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeDailyFeedLimitPopup();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeDailyFeedLimitPopup();
    });
  }

  document.getElementById('dailyFeedLimitText').textContent =
    `You have dispensed food ${pluralize(feedsToday, 'time')} today. Since rooms are limited, ` +
    `each user can only dispense food ${pluralize(maxFeedsPerDay, 'time')} per day to give a chance to other users.`;

  // Counted from when the response arrived, so the device clock being off doesn't matter
  const endTime = Date.now() + retryAfterSeconds * 1000;
  const update = () => {
    const remaining = Math.ceil((endTime - Date.now()) / 1000);
    if (remaining > 0) {
      countdownElement.textContent = `You will be able to feed again in ${formatDailyFeedLimitWait(remaining)}.`;
      return;
    }
    countdownElement.textContent = 'You can feed again now.';
    clearInterval(dailyFeedLimitInterval);
    dailyFeedLimitInterval = null;
  };

  clearInterval(dailyFeedLimitInterval);
  dailyFeedLimitInterval = setInterval(update, 250);
  update();

  if (!modal.classList.contains('active')) {
    modal.classList.add('active');
    modalOpen();
  }
}

function isChatDisabledByPermission() {
  const permission = currentStream?.message_permission;
  if (permission === 'DISABLED') return true;
  if (permission === 'ADMINS' && !currentStream?.user_is_admin) return true;
  return false;
}

function applyChatPermissionState() {
  const chatInput = document.querySelector('.chat-input');
  const chatSendButton = document.querySelector('.chat-send-button');
  if (!chatInput || !chatSendButton) return;

  if (isChatDisabledByPermission()) {
    chatInput.disabled = true;
    chatInput.placeholder = 'chat disabled for now';
    chatSendButton.disabled = true;
  } else {
    chatInput.disabled = false;
    chatInput.placeholder = 'type a message...';
    chatSendButton.disabled = false;
  }
}

function updateChatInputDisabled(disabled) {
  if (!disabled && isChatDisabledByPermission()) return;

  const chatInput = document.querySelector('.chat-input');
  const chatSendButton = document.querySelector('.chat-send-button');
  
  if (chatInput) {
    chatInput.disabled = disabled;
    if (disabled) {
      chatInput.placeholder = 'No spam pls';
    } else {
      chatInput.placeholder = 'type a message...';
    }
  }
  
  if (chatSendButton) {
    chatSendButton.disabled = disabled;
  }
}

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  
  if (h > 0) {
    return `${h} hour${h !== 1 ? 's' : ''}, ${m} minute${m !== 1 ? 's' : ''} and ${s} second${s !== 1 ? 's' : ''}`;
  } else if (m > 0) {
    return `${m} minute${m !== 1 ? 's' : ''} and ${s} second${s !== 1 ? 's' : ''}`;
  } else {
    return `${s} second${s !== 1 ? 's' : ''}`;
  }
}

function handleRateLimitError(message, retryAfter) {
  const messageElement = document.querySelector('.cooldown-message');
  
  if (!messageElement) {
    console.error('Message element not found');
    return;
  }

  // Clear any existing interval before starting a new one
  clearInterval(rateLimitInterval);

  // Disable chat input
  updateChatInputDisabled(true);

  messageElement.style.display = 'block';
  messageElement.style.opacity = '1';

  let remainingSeconds = Math.max(0, Math.floor(retryAfter));
  let messageVisibleTime = 5; // Show message for 5 seconds

  const updateMessage = () => {
    if (remainingSeconds <= 0) {
      clearInterval(rateLimitInterval);
      rateLimitInterval = null;
      // Re-enable chat input
      updateChatInputDisabled(false);
      return;
    }

    // Update message text only if still visible
    if (messageVisibleTime > 0) {
      const timeString = formatTime(remainingSeconds);
      messageElement.innerText = `Message limit exceeded. Please wait ${timeString} before sending another message.`;
    }

    remainingSeconds -= 1;
  };

  const hideMessage = () => {
    if (messageElement) {
      messageElement.style.opacity = '0';
      setTimeout(() => {
        if (messageElement) {
          messageElement.style.display = 'none';
        }
      }, 1000);
    }
  };

  // Initial update
  updateMessage();

  // Hide message after 5 seconds
  setTimeout(() => {
    messageVisibleTime = 0;
    hideMessage();
  }, 5000);

  // Update every second (continues even after message is hidden)
  rateLimitInterval = setInterval(updateMessage, 1000);
}

function hasDonationPopupCookie() {
  if (typeof document === 'undefined') return false;
  return document.cookie.split(';').some((entry) => {
    return entry.trim().startsWith(`${DONATION_POPUP_COOKIE_NAME}=`);
  });
}

function setDonationPopupCookie() {
  if (typeof document === 'undefined') return;
  const maxAge = DONATION_POPUP_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${DONATION_POPUP_COOKIE_NAME}=1; max-age=${maxAge}; path=/; samesite=lax`;
}

function clearDonationPopupTimer() {
  if (donationPopupTimeout) {
    clearTimeout(donationPopupTimeout);
    donationPopupTimeout = null;
  }
}

function hideDonationPopup() {
  const popup = document.getElementById('donationPopup');
  if (!popup) return;
  popup.style.display = 'none';
  popup.setAttribute('aria-hidden', 'true');
}

function showDonationPopup() {
  const popup = document.getElementById('donationPopup');
  if (!popup) return;
  setDonationPopupCookie();
  popup.style.display = 'flex';
  popup.setAttribute('aria-hidden', 'false');
}

function bindDonationPopup() {
  const popup = document.getElementById('donationPopup');
  if (!popup || popup.dataset.bound) return;
  popup.dataset.bound = 'true';
  const closeButton = popup.querySelector('.donation-popup__close');
  if (closeButton) {
    closeButton.addEventListener('click', (event) => {
      event.stopPropagation();
      hideDonationPopup();
    });
  }
}

function scheduleDonationPopup() {
  clearDonationPopupTimer();
  hideDonationPopup();
  if (hasDonationPopupCookie()) {
    return;
  }
  donationPopupTimeout = setTimeout(() => {
    if (!document.body.classList.contains('livestream-active')) return;
    showDonationPopup();
  }, DONATION_POPUP_DELAY_SECONDS * 1000);
}

function getFeedButtonsForRoom(roomId) {
  return document.querySelectorAll(`.feed-button[data-room-id="${roomId}"]`);
}

// Celebratory green particles that fall off the bottom of the screen on a successful feed
function celebrateFeed(originX, originY) {
  const container = document.createElement('div');
  container.classList.add('explode', 'explode--feed');
  document.body.appendChild(container);

  const chars = ['₊', '˚', '⊹'];
  const particleCount = 14;
  let remaining = particleCount;

  for (let i = 0; i < particleCount; i++) {
    const particle = document.createElement('p');
    particle.textContent = chars[Math.floor(Math.random() * chars.length)];
    container.appendChild(particle);

    let x = 0;
    let y = 0;
    let vx = (Math.random() - 0.5) * 3;
    let vy = -(0.4 + Math.random() * 0.4);
    const gravity = 0.01;

    particle.style.left = originX + 'px';
    particle.style.top = originY + 'px';

    const step = () => {
      vy += gravity;
      x += vx;
      y += vy;
      particle.style.transform = `translate(${x}px, ${y}px)`;

      if (originY + y > window.innerHeight + 50) {
        particle.remove();
        remaining -= 1;
        if (remaining <= 0) container.remove();
        return;
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}

function feedCat(roomId, originX, originY) {
  const feedButtons = getFeedButtonsForRoom(roomId);
  let responseStatus;
  let responseData;
      feedButtons.forEach(button => {
        button.disabled = true;
        button.innerHTML = '<span class="spinner"></span> feeding...';
        fitButtonText(button);
      });
      authFetch(`${apiUrl}/feed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userName: userName,
        roomId: roomId,
        deviceId: deviceId
      })
  })
  .then(response => {
    responseStatus = response.status;
    return response.json();
  })
  .then(data => {
      responseData = data;
      feedButtons.forEach(button => {
        button.disabled = false;
        button.innerHTML = '[feed the cat]';
        fitButtonText(button);
      });
  })
  .then(() => {
    if (responseStatus === 200) {
      successMessage('[cat fed successfully]');
      if (typeof originX === 'number' && typeof originY === 'number') {
        celebrateFeed(originX, originY);
      }
      playFeedNotificationSound();
      if (typeof responseData?.cooldown_seconds === 'number') {
        startFeedStatusCountdown(responseData.cooldown_seconds);
      }
    } else if (responseStatus === 429 && responseData?.error_type === 'DAILY_FEED_LIMIT_REACHED') {
      showDailyFeedLimitPopup(responseData.feeds_today, responseData.max_feeds_per_day, responseData.retry_after);
    } else if (responseStatus === 429) {
      cooldownMessage(responseData.cooldown_seconds, responseData.last_feed_time);
      if (typeof responseData?.cooldown_seconds === 'number') {
        // Do not restart the countdown here; a feed did not occur.
      }
    } else if (responseStatus === 403) {
      sleepMessage(responseData.night_schedule_time_seconds, responseData.night_schedule_start);
    }
  })
  .catch(error => {
      console.error('Error:', error);
      alert('Feeding the cat is disabled for now. Please try again later');
      feedButtons.forEach(button => {
        button.disabled = false;
        button.innerHTML = '[feed the cat]';
        fitButtonText(button);
      });
  })
}

function handleFeedButtonClick(event) {
  const button = event.currentTarget;
  const roomId = button.dataset.roomId;
  if (isFeedingDisabledEntirely()) return;
  if (currentStream?.feeding_permission === 'AUTH_USERS' && !isLoggedIn()) {
    openAuthModal({ message: 'You need to sign in to feed the cat' });
    return;
  }
  if (!canFeedInCurrentRoom()) return;
  if (roomId) {
    let originX = event.clientX;
    let originY = event.clientY;
    if (!originX && !originY) {
      const rect = button.getBoundingClientRect();
      originX = rect.left + rect.width / 2;
      originY = rect.top + rect.height / 2;
    }
    feedCat(roomId, originX, originY);
  }
}

function updateMusicIcons() {
  const slider = document.getElementById('volumeSlider');
  let volumeValue = slider ? Number(slider.value) : getCurrentMusicVolumePercent();
  if (!Number.isFinite(volumeValue)) {
    volumeValue = getCurrentMusicVolumePercent();
  }

  const desktopIcon = document.getElementById('desktopVolumeIcon');
  if (desktopIcon) {
    if (volumeValue <= 0) {
      desktopIcon.src = VOLUME_ICON_MUTED;
    } else if (volumeValue <= 60) {
      desktopIcon.src = VOLUME_ICON_LOW;
    } else {
      desktopIcon.src = VOLUME_ICON_HIGH;
    }
  }

  const mobileIconSrc = volumeValue <= 0 ? VOLUME_ICON_MUTED : VOLUME_ICON_HIGH;
  ['mobileMusicToggle', 'mobileMusicToggleSecondary', 'desktopMusicToggle', 'mobileMusicToggleHeader'].forEach(id => {
    const button = document.getElementById(id);
    const iconElement = button?.querySelector('.music-toggle-icon');
    if (iconElement) iconElement.src = mobileIconSrc;
  });
}

function bindMusicControls() {
  const slider = document.getElementById('volumeSlider');
  if (slider && !slider.dataset.musicBound) {
    slider.dataset.musicBound = 'true';
    slider.addEventListener('input', () => {
      musicVolume();
      updateMarqueePlayState();
      updateMusicIcons();
    });
  }

  const bindToggle = (id) => {
    const button = document.getElementById(id);
    if (button && !button.dataset.musicBound) {
      button.dataset.musicBound = 'true';
      button.addEventListener('click', () => {
        toggleMusic();
        updateMarqueePlayState();
        updateMusicIcons();
      });
    }
  };

  ['mobileMusicToggle', 'mobileMusicToggleSecondary', 'desktopMusicToggle', 'mobileMusicToggleHeader'].forEach(bindToggle);

  musicControlsBound = true;
}

function applyDisplayName(el) {
  userName = getDisplayName();
  el.classList.remove('is-loading');
  el.textContent = userName;
  if (isLoggedIn()) {
    el.classList.add('is-logged-in');
    el.classList.remove('is-guest');
  } else {
    el.classList.remove('is-logged-in');
    el.classList.add('is-guest');
  }
}

function bindUserDisplayName() {
  const userDisplayNameElement = document.querySelector('.user-display-name');
  if (!userDisplayNameElement) return;

  if (userDisplayNameElement.dataset.nameBound) {
    if (!userDisplayNameElement.classList.contains('is-loading')) {
      applyDisplayName(userDisplayNameElement);
    }
    return;
  }
  userDisplayNameElement.dataset.nameBound = 'true';

  waitForAuth().then(() => applyDisplayName(userDisplayNameElement));

  initAuthModal();
  initProfileModal();
  initUserCardModal();

  userDisplayNameElement.addEventListener('click', () => {
    if (isLoggedIn()) {
      openProfileModal();
    } else {
      openAuthModal();
    }
  });

  onAuthChange(() => {
    applyDisplayName(userDisplayNameElement);
    if (currentStream) {
      if (canFeedInCurrentRoom()) {
        updateFeedButtonsDisabled(false);
      } else {
        updateFeedButtonsDisabled(true, '', FEEDING_DISABLED_MESSAGE);
      }
      applyChatPermissionState();
    }
  });
}

function clearFeedStatusCountdown({ reset = false } = {}) {
  if (feedStatusInterval) {
    clearInterval(feedStatusInterval);
    feedStatusInterval = null;
  }
  if (reset) {
    updateFeedButtonsDisabled(false);
  }
}

function formatFeedCountdown(seconds) {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  if (hours > 0) {
    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    const ss = String(secs).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  }
  const mm = String(minutes).padStart(2, '0');
  const ss = String(secs).padStart(2, '0');
  return `${mm}:${ss}`;
}

function startFeedStatusCountdown(initialSeconds) {
  clearFeedStatusCountdown({ reset: false });

  if (!canFeedInCurrentRoom()) {
    updateFeedButtonsDisabled(true, '', FEEDING_DISABLED_MESSAGE);
    return;
  }

  const seconds = Math.floor(Number(initialSeconds));
  if (!Number.isFinite(seconds) || seconds <= 0) {
    updateFeedButtonsDisabled(false);
    return;
  }

  const endTime = Date.now() + seconds * 1000;
  let lastDisplayed = -1;

  const update = () => {
    const remaining = Math.ceil((endTime - Date.now()) / 1000);
    if (remaining <= 0) {
      updateFeedButtonsDisabled(false);
      clearFeedStatusCountdown();
      return;
    }
    if (remaining !== lastDisplayed) {
      lastDisplayed = remaining;
      updateFeedButtonsDisabled(true, formatFeedCountdown(remaining));
    }
  };

  update();
  feedStatusInterval = setInterval(update, 250);
}

function updateMusicHeaderHeight() {
  const header = document.querySelector('.music-header');
  if (!header) return;
  const height = header.offsetHeight;
  if (height > 0) {
    document.documentElement.style.setProperty('--music-header-height', `${height}px`);
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || height;
    const availableHeight = Math.max(0, viewportHeight - height);
    const maxWidth = Math.max(320, (availableHeight * 16) / 9);
    document.documentElement.style.setProperty('--livestream-max-width', `${maxWidth}px`);
  }
}

export function initializeMusic() {
  bindMusicControls();
  bindUserDisplayName();
  bindRadioVisibilitySync();
 
  updateMusicHeaderHeight();
  requestAnimationFrame(updateMusicHeaderHeight);
  if (!musicHeaderResizeBound) {
    musicHeaderResizeBound = true;
    window.addEventListener('resize', updateMusicHeaderHeight);
  }

  if (!musicInitialized) {
    musicInitialized = true;
    const slider = document.getElementById('volumeSlider');
    if (slider) {
      slider.value = '0';
    }
    lastSetVolume = 0;
    music.volume = 0;
    music.pause();
  }
  updateMarqueePlayState();
  updateMusicIcons();
  updatePassiveRadioSyncTimer();
  startPassiveSongInfoUpdates();
  bindSongModal();
}

// Music functionality
const music = new Audio();
music.preload = 'none';

const R2_BASE = 'https://music.feed-the-cat.stream';

function getSongSource(song) {
  const url = song.url;
  if (window.location.hostname === 'localhost' && url.startsWith(R2_BASE)) {
    return url.replace(R2_BASE, '/r2-music');
  }
  return url;
}

const RADIO_START_TIME_MS = Date.UTC(2024, 10, 1, 0, 0, 0);
const RADIO_DEFAULT_TRACK_DURATION = 180;
const RADIO_RESYNC_TOLERANCE_SECONDS = 1.25;
const RADIO_PASSIVE_SYNC_INTERVAL_MS = 20_000;
const RADIO_MIN_NEXT_SYNC_DELAY_MS = 2_000;
const RADIO_MAX_NEXT_SYNC_DELAY_MS = 15 * 60 * 1000;
const RADIO_SONG_INDEXES = songs
  .map((song, index) => (song.active && song.url ? index : null))
  .filter((index) => index !== null);
const RADIO_SHUFFLE_SIGNATURE = getPlayableSongs()
  .map((song) => `${song.name}|${song.path}`)
  .join('||');
const RADIO_SHUFFLE_BASE_SEED = hashStringToSeed(RADIO_SHUFFLE_SIGNATURE);

let playlistDurations = null;
let playlistTotalDuration = 0;
let radioSyncPromise = null;
let radioPassiveSyncInterval = null;
let radioNextTrackTimeout = null;
let cachedShuffleCycleIndex = null;
let cachedShuffleOrder = null;
let passiveSongInfoTimeout = null;

function enableRadioPlayback() {
  if (!radioUnlockedByUser) {
    radioUnlockedByUser = true;
    music.preload = 'auto';
  }
  stopPassiveSongInfoUpdates();
}

function shouldSkipRadioSync() {
  if (!radioUnlockedByUser) {
    return true;
  }
  return getVolumeSliderValue() <= 0;
}

function getVolumeSliderElement() {
  return document.querySelector('.volume-slider') || document.getElementById('volumeSlider');
}

function getCurrentMusicVolumePercent() {
  const volume = Number(music.volume);
  if (!Number.isFinite(volume) || volume <= 0) {
    return 0;
  }
  return Math.round(Math.max(0, Math.min(1, volume)) * 100);
}

function getVolumeSliderValue() {
  const sliderEl = getVolumeSliderElement();
  if (!sliderEl) {
    return getCurrentMusicVolumePercent();
  }
  const value = Number(sliderEl.value);
  return Number.isFinite(value) ? value : getCurrentMusicVolumePercent();
}

function hashStringToSeed(str) {
  if (!str) return 0x9E3779B9;
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (Math.imul(31, hash) + str.charCodeAt(i)) >>> 0;
  }
  return hash || 0x9E3779B9;
}

function createMulberry32(initialSeed) {
  let seed = initialSeed >>> 0;
  return () => {
    seed += 0x6D2B79F5;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function normalizeCycleSeed(value) {
  const normalized = ((value % 0x100000000) + 0x100000000) % 0x100000000;
  return normalized >>> 0;
}

function getShuffleSeedForCycle(cycleIndex) {
  const normalizedCycle = normalizeCycleSeed(cycleIndex);
  const seed = (RADIO_SHUFFLE_BASE_SEED ^ normalizedCycle) >>> 0;
  return seed || 0xA5A5A5A5;
}

function getRadioShuffleOrder(cycleIndex) {
  if (cachedShuffleCycleIndex === cycleIndex && cachedShuffleOrder) {
    return cachedShuffleOrder;
  }
  const order = RADIO_SONG_INDEXES.slice();
  const rng = createMulberry32(getShuffleSeedForCycle(cycleIndex));
  for (let i = order.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const temp = order[i];
    order[i] = order[j];
    order[j] = temp;
  }
  cachedShuffleCycleIndex = cycleIndex;
  cachedShuffleOrder = order;
  return order;
}

function bindRadioVisibilitySync() {
  if (radioVisibilitySyncBound) return;
  if (typeof document === 'undefined') return;

  const handleVisibilityResume = () => {
    if (document.visibilityState && document.visibilityState !== 'visible') {
      return;
    }
    if (shouldSkipRadioSync()) {
      return;
    }
    const autoplay = !music.paused && getVolumeSliderValue() > 0;
    syncMusicToRadio({ autoplay, force: true });
  };

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      handleVisibilityResume();
    }
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('focus', handleVisibilityResume);
  }

  radioVisibilitySyncBound = true;
}

function updatePassiveRadioSyncTimer() {
  const needsPassiveSync = !shouldSkipRadioSync() && music.paused;
  if (needsPassiveSync && !radioPassiveSyncInterval) {
    radioPassiveSyncInterval = setInterval(() => {
      syncMusicToRadio({ autoplay: false });
    }, RADIO_PASSIVE_SYNC_INTERVAL_MS);
  } else if (!needsPassiveSync && radioPassiveSyncInterval) {
    clearInterval(radioPassiveSyncInterval);
    radioPassiveSyncInterval = null;
  }
}

function clearRadioNextTrackSync() {
  if (radioNextTrackTimeout) {
    clearTimeout(radioNextTrackTimeout);
    radioNextTrackTimeout = null;
  }
}

function scheduleRadioNextTrackSync(remainingSeconds) {
  clearRadioNextTrackSync();

  if (shouldSkipRadioSync()) {
    return;
  }

  if (!Number.isFinite(remainingSeconds)) {
    return;
  }

  const delayMs = Math.min(
    RADIO_MAX_NEXT_SYNC_DELAY_MS,
    Math.max(RADIO_MIN_NEXT_SYNC_DELAY_MS, remainingSeconds * 1000)
  );

  radioNextTrackTimeout = setTimeout(() => {
    if (shouldSkipRadioSync()) {
      clearRadioNextTrackSync();
      return;
    }
    const autoplay = !music.paused && getVolumeSliderValue() > 0;
    syncMusicToRadio({ autoplay, force: true });
  }, delayMs);
}

function stopPassiveSongInfoUpdates() {
  if (passiveSongInfoTimeout) {
    clearTimeout(passiveSongInfoTimeout);
    passiveSongInfoTimeout = null;
  }
}

function schedulePassiveSongInfoUpdate(delaySeconds = 0) {
  stopPassiveSongInfoUpdates();
  if (!shouldSkipRadioSync()) {
    return;
  }
  const safeDelay = Math.max(0, Number(delaySeconds) || 0);
  passiveSongInfoTimeout = setTimeout(handlePassiveSongInfoTick, safeDelay * 1000);
}

function handlePassiveSongInfoTick() {
  passiveSongInfoTimeout = null;
  if (!shouldSkipRadioSync()) {
    stopPassiveSongInfoUpdates();
    return;
  }

  const timeline = getTimelineDurationsForDisplay();
  if (!timeline) {
    schedulePassiveSongInfoUpdate(30);
    return;
  }

  const state = computeRadioState(Date.now(), timeline.durations, timeline.totalDuration);
  if (!state) {
    schedulePassiveSongInfoUpdate(30);
    return;
  }

  const song = songs[state.songIndex];
  if (song) {
    renderSongInfo(song);
  }

  const trackDuration = timeline.durations?.[state.songIndex] ?? RADIO_DEFAULT_TRACK_DURATION;
  const remainingSeconds = Math.max(5, trackDuration - state.offsetSeconds);
  schedulePassiveSongInfoUpdate(remainingSeconds);
}

function startPassiveSongInfoUpdates() {
  if (!shouldSkipRadioSync()) {
    stopPassiveSongInfoUpdates();
    return;
  }
  stopPassiveSongInfoUpdates();
  handlePassiveSongInfoTick();
}

let currentDisplayedSong = null;

function renderSongInfo(song) {
  if (!song || typeof window === 'undefined') return;
  currentDisplayedSong = song;
  if (isSongModalOpen()) updateSongModalContent(song);
  const songInfoContainer = document.querySelector('.song-info');
  const text = `${song.artist} - <strong>${song.name}</strong>`;
  const noteIcon = `<span class="marquee__icon"><img src="/icons/music-note.svg" alt="" aria-hidden="true"></span>`;

  if (songInfoContainer) {
    songInfoContainer.style.removeProperty('--marquee-duration');
    songInfoContainer.classList.remove('is-overflowing');
    const marqueeItem = `<span class="marquee__inner">${noteIcon} <span class="marquee__text">${text}</span></span>`;
    songInfoContainer.innerHTML = `<span class="marquee marquee--static"><span class="marquee__track">${Array(4).fill(marqueeItem).join(' ')}</span></span>`;

    requestAnimationFrame(() => {
      const innerOne = songInfoContainer.querySelector('.marquee__inner');
      const track = songInfoContainer.querySelector('.marquee__track');
      const marquee = songInfoContainer.querySelector('.marquee');
      if (!innerOne || !track || !marquee) return;
      const textWidth = innerOne.getBoundingClientRect().width;
      const containerWidth = songInfoContainer.getBoundingClientRect().width;
      const pxPerSecond = 40;
      const forceMarquee = window.innerWidth <= 900;
      const duration = Math.max(8, (textWidth * 2) / pxPerSecond);
      if (forceMarquee || textWidth > containerWidth) {
        marquee.classList.remove('marquee--static');
        songInfoContainer.classList.add('is-overflowing');
        songInfoContainer.style.setProperty('--marquee-duration', `${duration}s`);
        songInfoContainer.style.setProperty('--marquee-translate', `-${textWidth}px`);
        track.style.width = `${textWidth * 2}px`;
      } else {
        marquee.classList.add('marquee--static');
        songInfoContainer.classList.remove('is-overflowing');
        songInfoContainer.style.removeProperty('--marquee-duration');
        songInfoContainer.style.removeProperty('--marquee-translate');
        track.style.removeProperty('width');
      }
    });
  }
}

let songModalBound = false;

function updateSongModalContent(song) {
  if (!song) return;

  document.getElementById('songModalTitleText').textContent = song.name;
  document.getElementById('songModalArtist').textContent = song.artist;

  const img = document.getElementById('songModalImage');
  const placeholder = document.getElementById('songModalPlaceholder');
  if (song.imageUrl) {
    img.style.display = 'none';
    placeholder.style.display = '';
    img.onload = () => { img.style.display = ''; placeholder.style.display = 'none'; };
    img.onerror = () => { img.style.display = 'none'; placeholder.style.display = ''; };
    img.src = song.imageUrl;
    img.alt = `${song.name} by ${song.artist}`;
  } else {
    img.removeAttribute('src');
    img.style.display = 'none';
    placeholder.style.display = '';
  }

  const link = document.getElementById('songModalLink');
  const imageLink = document.getElementById('songModalImageLink');
  if (song.sourceUrl) {
    link.href = song.sourceUrl;
    link.classList.remove('hidden');
    imageLink.href = song.sourceUrl;
    imageLink.style.cursor = 'pointer';
  } else {
    link.removeAttribute('href');
    link.classList.add('hidden');
    imageLink.removeAttribute('href');
    imageLink.style.cursor = 'default';
  }
}

function isSongModalOpen() {
  const songModal = document.getElementById('songModal');
  return songModal && songModal.classList.contains('active');
}

function openSongModal() {
  const songModal = document.getElementById('songModal');
  const song = currentDisplayedSong;
  if (!song || !songModal) return;

  updateSongModalContent(song);
  songModal.classList.add('active');
  modalOpen();
}

function closeSongModal() {
  const songModal = document.getElementById('songModal');
  if (!songModal || !songModal.classList.contains('active')) return;
  songModal.classList.remove('active');
  modalClose();
}

function bindSongModal() {
  if (songModalBound) return;
  songModalBound = true;

  const songInfoContainer = document.querySelector('.song-info');
  if (songInfoContainer) {
    songInfoContainer.addEventListener('click', (e) => {
      e.preventDefault();
      openSongModal();
    });
  }

  const closeSongModalBtn = document.getElementById('closeSongModal');
  if (closeSongModalBtn) {
    closeSongModalBtn.addEventListener('click', closeSongModal);
  }

  const songModal = document.getElementById('songModal');
  if (songModal) {
    songModal.addEventListener('click', (e) => {
      if (e.target === songModal) closeSongModal();
    });
  }

  document.addEventListener('keydown', (e) => {
    const modal = document.getElementById('songModal');
    if (e.key === 'Escape' && modal && modal.classList.contains('active')) {
      closeSongModal();
    }
  });
}

function buildPlaylistDurations() {
  if (playlistDurations && playlistTotalDuration > 0) {
    return { durations: playlistDurations, totalDuration: playlistTotalDuration };
  }
  const durations = songs.map((song) => {
    if (!song.active || !song.url) {
      return 0;
    }
    if (Number.isFinite(song.duration) && song.duration > 1) {
      return song.duration;
    }
    return RADIO_DEFAULT_TRACK_DURATION;
  });
  const totalDuration = durations.reduce((sum, value) => sum + value, 0);
  playlistDurations = durations;
  playlistTotalDuration = totalDuration;
  return { durations, totalDuration };
}

function ensurePlaylistDurations() {
  return Promise.resolve(buildPlaylistDurations());
}

function getTimelineDurationsForDisplay() {
  return buildPlaylistDurations();
}

function computeRadioState(nowMs, durations, totalDuration) {
  if (!Array.isArray(durations) || !durations.length || !Number.isFinite(totalDuration) || totalDuration <= 0) {
    return null;
  }

  const totalMs = totalDuration * 1000;
  if (totalMs <= 0) return null;

  const elapsedMs = nowMs - RADIO_START_TIME_MS;
  if (!Number.isFinite(elapsedMs)) return null;

  const cycleIndex = Math.floor(elapsedMs / totalMs);
  let offsetMs = elapsedMs % totalMs;
  if (offsetMs < 0) {
    offsetMs += totalMs;
  }

  const shuffleOrder = getRadioShuffleOrder(cycleIndex);
  if (!shuffleOrder || !shuffleOrder.length) {
    return null;
  }

  let cumulative = 0;
  for (let orderPosition = 0; orderPosition < shuffleOrder.length; orderPosition += 1) {
    const songIndex = shuffleOrder[orderPosition];
    const songDurationSeconds = durations[songIndex];
    const safeDurationSeconds = Number.isFinite(songDurationSeconds) && songDurationSeconds > 0
      ? songDurationSeconds
      : RADIO_DEFAULT_TRACK_DURATION;
    const songDurationMs = safeDurationSeconds * 1000;
    if (offsetMs < cumulative + songDurationMs) {
      const offsetSeconds = (offsetMs - cumulative) / 1000;
      return {
        songIndex,
        offsetSeconds: Math.max(0, offsetSeconds),
        cycleIndex,
        orderPosition,
      };
    }
    cumulative += songDurationMs;
  }

  const lastSongIndex = shuffleOrder[shuffleOrder.length - 1];
  return {
    songIndex: lastSongIndex,
    offsetSeconds: 0,
    cycleIndex,
    orderPosition: shuffleOrder.length - 1,
  };
}

function normalizeSongUrl(path) {
  if (typeof window === 'undefined') return path;
  try {
    return new URL(path, window.location.origin).href;
  } catch (_) {
    return path;
  }
}

function waitForMusicMetadata() {
  if (music.readyState >= 1) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const cleanup = () => {
      music.removeEventListener('loadedmetadata', onLoaded);
      music.removeEventListener('error', onError);
    };
    const onLoaded = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      resolve();
    };
    music.addEventListener('loadedmetadata', onLoaded, { once: true });
    music.addEventListener('error', onError, { once: true });
  });
}

async function setSongSourceAtOffset(song, offsetSeconds) {
  if (!song) return;
  const desiredUrl = normalizeSongUrl(getSongSource(song));
  const currentUrl = music.currentSrc || music.src;
  const needsNewSource = !currentUrl || currentUrl !== desiredUrl;

  if (needsNewSource) {
    await new Promise((resolve) => {
      const cleanup = () => {
        music.removeEventListener('loadedmetadata', onLoaded);
        music.removeEventListener('error', onError);
      };
      const onLoaded = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        resolve();
      };
      music.addEventListener('loadedmetadata', onLoaded, { once: true });
      music.addEventListener('error', onError, { once: true });
      music.src = getSongSource(song);
      music.load();
    });
  } else if (music.readyState < 1) {
    await waitForMusicMetadata();
  }

  const effectiveDuration = Number.isFinite(music.duration) && music.duration > 0
    ? music.duration
    : song.duration || 0;
  const maxSeek = effectiveDuration > 0 ? Math.max(0, effectiveDuration - 0.25) : 0;
  const safeOffset = effectiveDuration > 0 ? Math.min(Math.max(offsetSeconds, 0), maxSeek) : 0;

  try {
    music.currentTime = safeOffset;
  } catch (err) {
    console.warn('Unable to update radio playback position', err);
  }
}

function shouldAutoplay(firstPlay) {
  if (!firstPlay) return true;
  return getVolumeSliderValue() > 0;
}

function needsSongSourceUpdate(song, offsetSeconds) {
  if (!song) return true;
  const desiredUrl = normalizeSongUrl(getSongSource(song));
  const currentUrl = music.currentSrc || music.src;
  if (!currentUrl || currentUrl !== desiredUrl) {
    return true;
  }
  if (music.readyState < 1) {
    return true;
  }
  if (!Number.isFinite(offsetSeconds)) {
    return false;
  }
  const currentTime = Number.isFinite(music.currentTime) ? music.currentTime : 0;
  return Math.abs(currentTime - offsetSeconds) > RADIO_RESYNC_TOLERANCE_SECONDS;
}

const MUSIC_LOADING_SPINNER_DELAY_MS = 500;
let musicLoadingSpinnerTimer = null;

function applyMusicLoadingState(isLoading) {
  ['mobileMusicToggle', 'mobileMusicToggleSecondary', 'desktopMusicToggle', 'mobileMusicToggleHeader'].forEach(id => {
    document.getElementById(id)?.classList.toggle('is-music-loading', isLoading);
  });
  document.querySelector('.music-controls .volume-control')?.classList.toggle('is-music-loading', isLoading);
}

function scheduleMusicLoadingSpinner() {
  clearMusicLoadingSpinner();
  musicLoadingSpinnerTimer = setTimeout(() => {
    musicLoadingSpinnerTimer = null;
    applyMusicLoadingState(true);
  }, MUSIC_LOADING_SPINNER_DELAY_MS);
}

function clearMusicLoadingSpinner() {
  if (musicLoadingSpinnerTimer) {
    clearTimeout(musicLoadingSpinnerTimer);
    musicLoadingSpinnerTimer = null;
  }
  applyMusicLoadingState(false);
}

function syncMusicToRadio(options = {}) {
  const { firstPlay = false, autoplay = true, force = false } = options;

  if (shouldSkipRadioSync()) {
    startPassiveSongInfoUpdates();
    return Promise.resolve();
  }

  stopPassiveSongInfoUpdates();

  if (radioSyncPromise && !force) {
    return radioSyncPromise;
  }

  // Show a loading spinner if the song takes more than 0.5s to start playing
  // (e.g. the first time the user unmutes, while the audio downloads).
  const willAttemptPlayback = autoplay && shouldAutoplay(firstPlay) && music.paused;
  if (willAttemptPlayback) {
    scheduleMusicLoadingSpinner();
  }

  radioSyncPromise = (async () => {
    const playlist = await ensurePlaylistDurations();
    const state = computeRadioState(Date.now(), playlist.durations, playlist.totalDuration);
    if (!state) {
      scheduleRadioNextTrackSync(null);
      return;
    }

    const song = songs[state.songIndex];
    if (!song) return;

    const shouldUpdateSource = needsSongSourceUpdate(song, state.offsetSeconds);
    if (shouldUpdateSource) {
      await setSongSourceAtOffset(song, state.offsetSeconds);
    }
    renderSongInfo(song);
    const trackDuration = playlist.durations?.[state.songIndex] ?? song.duration ?? RADIO_DEFAULT_TRACK_DURATION;
    const remainingSeconds = Math.max(0, trackDuration - state.offsetSeconds);
    scheduleRadioNextTrackSync(remainingSeconds);

    const allowAutoplay = autoplay && shouldAutoplay(firstPlay);
    if (allowAutoplay && music.paused) {
      try {
        await music.play();
      } catch (err) {
        console.warn('Unable to start music playback', err);
      }
    }

    updateMarqueePlayState();
    updateMusicIcons();
  })().catch((err) => {
    console.error('Failed to synchronize radio playback', err);
  }).finally(() => {
    radioSyncPromise = null;
    clearMusicLoadingSpinner();
  });

  return radioSyncPromise;
}

music.addEventListener('ended', () => {
  if (shouldSkipRadioSync()) {
    return;
  }
  syncMusicToRadio({ autoplay: true, force: true });
});
music.addEventListener('play', handleMusicPlayStateChange);
music.addEventListener('pause', handleMusicPlayStateChange);

function updateMarqueePlayState() {
  const container = document.querySelector('.song-info');
  if (!container) return;
  container.classList.add('is-animating');
}

function handleMusicPlayStateChange() {
  updateMarqueePlayState();
  updatePassiveRadioSyncTimer();
}

function musicVolume() {
  const sliderEl = document.getElementById('volumeSlider');
  if (!sliderEl) return;
  const volume_level = Number(sliderEl.value);
  if (volume_level > 0) {
    lastSetVolume = volume_level;
    music.volume = volume_level / 100;
    enableRadioPlayback();
    const feedNotificationSound = new Audio('/sounds/feed_notification.mp3');
    const chatNotificationSound = new Audio('/sounds/chat_notification.mp3');
    feedNotificationSound.volume = Math.max(0.2, volume_level / 100);
    chatNotificationSound.volume = Math.max(0.2, volume_level / 100);
    if (music.paused) {
      syncMusicToRadio({ autoplay: true }).catch((err) => {
        console.warn('Unable to resume radio playback', err);
      });
    }
    stopPassiveSongInfoUpdates();
  } else {
    music.volume = 0;
    music.pause();
    clearRadioNextTrackSync();
    const feedNotificationSound = new Audio('/sounds/feed_notification.mp3');
    const chatNotificationSound = new Audio('/sounds/chat_notification.mp3');
    feedNotificationSound.volume = 0;
    chatNotificationSound.volume = 0;
    startPassiveSongInfoUpdates();
  }
  updateMusicIcons();
  updatePassiveRadioSyncTimer();
}

function toggleMusic() {
  const slider = document.getElementById('volumeSlider');
  if (!slider) {
    if (music.paused) {
      const restoredVolume = lastSetVolume > 0 ? lastSetVolume : DEFAULT_VOLUME;
      lastSetVolume = restoredVolume;
      music.volume = restoredVolume / 100;
      enableRadioPlayback();
      syncMusicToRadio({ autoplay: true }).catch((err) => {
        console.warn('Unable to resume radio playback', err);
      });
      stopPassiveSongInfoUpdates();
    } else {
      music.pause();
      music.volume = 0;
      clearRadioNextTrackSync();
      startPassiveSongInfoUpdates();
    }
    updateMusicIcons();
    updatePassiveRadioSyncTimer();
    return;
  }

  const currentVolume = Number(slider.value);
  const isMuted = music.paused || currentVolume === 0 || music.volume === 0;

  if (isMuted) {
    const restoredVolume = lastSetVolume > 0 ? lastSetVolume : DEFAULT_VOLUME;
    slider.value = String(restoredVolume);
    musicVolume();
  } else {
    slider.value = '0';
    musicVolume();
  }
  updateMusicIcons();
  updatePassiveRadioSyncTimer();
}

// Show/hide room banner
function applyRoomBanner(stream) {
  const roomBanner = document.getElementById('roomBanner');
  if (roomBanner) {
    const bannerContent = document.getElementById('roomBannerContent');
    if (stream.banner && stream.banner.trim() && stream.banner_visible) {
      if (bannerContent) bannerContent.innerHTML = stream.banner.trim();
      roomBanner.style.display = 'block';
      roomBanner.setAttribute('aria-hidden', 'false');
      const closeBtn = roomBanner.querySelector('.room-banner__close');
      if (closeBtn && !closeBtn.dataset.bound) {
        closeBtn.dataset.bound = 'true';
        closeBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          roomBanner.style.display = 'none';
          roomBanner.setAttribute('aria-hidden', 'true');
        });
      }
    } else {
      roomBanner.style.display = 'none';
      roomBanner.setAttribute('aria-hidden', 'true');
    }
  }
}

function applyLivestreamRoomHeader(stream) {
  const nameEl = document.getElementById('roomName');
  const lockEl = document.getElementById('roomNameLock');
  if (nameEl) {
    nameEl.textContent = stream?.display_name ? stream.display_name : '';
  }
  if (lockEl) {
    const show = stream && stream.visible === false;
    lockEl.toggleAttribute('hidden', !show);
    if (show) {
      lockEl.title = 'Room hidden';
    } else {
      lockEl.removeAttribute('title');
    }
  }
}

// Update info modal content with stream description
function updateInfoModalContent(stream) {
  const descriptionElement = document.getElementById('infoModalDescription');
  const titleElement = document.getElementById('infoModalTitle');
  const roomNameElement = document.getElementById('infoModalRoomName');
  const statsContainer = document.querySelector('.info-modal-stats');
  
  const title = stream.display_name;
  const description = stream.description;
  
  if (titleElement) {
    titleElement.textContent = title || 'Stream Info';
  }

  if (roomNameElement) {
    const roomName = stream.name;
    if (roomName) {
      roomNameElement.textContent = `/${roomName}`;
      roomNameElement.removeAttribute('hidden');
    } else {
      roomNameElement.textContent = '';
      roomNameElement.hidden = true;
    }
  }

  if (descriptionElement) {
  // If description exists and is not empty, use it; otherwise show a default message
  if (description && typeof description === 'string' && description.trim()) {
    descriptionElement.innerHTML = description.trim();
  } else {
    descriptionElement.innerHTML = '<p><i>No description available.</i></p>';
    }
  }

  if (statsContainer) {
    const timesFed = stream.times_fed !== undefined ? stream.times_fed : '0';
    let creationDateStr = 'Unknown';
      
    if (stream.creation_date) {
        try {
          // Ensure consistent date parsing (assuming UTC if no timezone provided)
          const datePart = stream.creation_date.replace(' ', 'T');
          const creationDate = new Date(datePart.endsWith('Z') ? datePart : datePart + 'Z');
          creationDateStr = creationDate.toLocaleDateString(DATE_LOCALE, { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          });
        } catch (e) {
            console.error("Error parsing creation date", e);
            creationDateStr = stream.creation_date;
        }
    }

    // Calculate kibble dispensed
    let kibbleAmountStr = '';
    if (stream.times_fed !== undefined) {
        const totalKg = (stream.times_fed * 5 / 1000).toFixed(2).replace(/\.00$/, '');
        kibbleAmountStr = totalKg;
    }

    const locationParts = [stream.city, stream.country]
      .map((x) => (typeof x === 'string' ? x.trim() : ''))
      .filter(Boolean);
    const locationStr = locationParts.join(', ');
    const locationBlock = locationStr
      ? `<div class="profile-stats-summary-line"><span class="usercard-stats-caret">&gt;</span> Located in <strong>${locationStr}</strong></div>`
      : '';
    statsContainer.innerHTML =
      `<div class="profile-stats-summary-line"><span class="usercard-stats-caret">&gt;</span> About <strong>${kibbleAmountStr}kg</strong> of kibble dispensed</div>` +
      `<div class="profile-stats-summary-line"><span class="usercard-stats-caret">&gt;</span> Operating since <strong>${creationDateStr}</strong></div>` +
      locationBlock;
  }
}

// While the room's data is fetched, so the previous room's chat doesn't stay on screen
export function showRoomLoadingState() {
  showLoading();
  showChatLoadingSkeleton(document.querySelector('.chat-messages'));
}

// Main initialization function
export async function initializeLivestreamComponents(stream) {
  document.body.classList.add('livestream-active');
  const root = document.getElementById('livestream-view');
  if (root) {
    root.classList.add('is-active');
  }
  
  // Clear any previous countdown interval (but don't reset button state yet)
  clearFeedStatusCountdown({ reset: false });
  leaderboardLoaded = false;
  
  currentStream = stream;
  
  const chatMessages = document.querySelector('.chat-messages');
  if (chatMessages) {
    showChatLoadingSkeleton(chatMessages);
  }

  applyChatPermissionState();
  
  // Set button roomId immediately so updateFeedButtonsDisabled works
  const feedButtons = document.querySelectorAll('.feed-button');
  feedButtons.forEach(button => {
    button.dataset.roomId = stream.name;
  });
  
  // Update button state immediately based on stream's feed status
  const secondsLeft = Number(stream?.seconds_left_to_feed);
  if (!canFeedInCurrentRoom()) {
    updateFeedButtonsDisabled(true, '', FEEDING_DISABLED_MESSAGE);
  } else if (Number.isFinite(secondsLeft) && secondsLeft > 0) {
    updateFeedButtonsDisabled(true, formatFeedCountdown(secondsLeft));
  } else {
    updateFeedButtonsDisabled(false);
  }
  
  // Show/hide visibility banner based on stream.stream_working
  const visibilityBanner = document.querySelector('.livestream-visibility-banner');
  if (visibilityBanner) {
    let closeButton = visibilityBanner.querySelector('.livestream-visibility-banner__close');
    if (!closeButton) {
      closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'close-modal livestream-visibility-banner__close';
      closeButton.setAttribute('aria-label', 'Dismiss notice');
      closeButton.textContent = '[x]';
      visibilityBanner.appendChild(closeButton);
    }
    if (!closeButton.dataset.bound) {
      closeButton.dataset.bound = 'true';
      closeButton.addEventListener('click', (event) => {
        event.stopPropagation();
        visibilityBanner.style.display = 'none';
      });
    }

    if (stream.stream_working === false && !stream.archived) {
      visibilityBanner.style.display = 'block';
    } else {
      visibilityBanner.style.display = 'none';
    }
  }

  applyRoomBanner(stream);

  // Reset camera to primary on room entry
  usingSecondaryStream = false;

  // Show/hide swap camera button
  const swapCameraBtn = document.getElementById('swapCameraButton');
  if (swapCameraBtn) {
    if (stream.secondary_stream_url && !stream.archived) {
      swapCameraBtn.style.display = 'flex';
      if (!swapCameraBtn.dataset.bound) {
        swapCameraBtn.dataset.bound = 'true';
        swapCameraBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          swapCamera();
        });
      }
    } else {
      swapCameraBtn.style.display = 'none';
    }
  }

  // Initialize the stream
  if (archivedMessage) archivedMessage.style.display = stream.archived ? 'flex' : 'none';
  if (stream.archived) {
    // Going straight from one room to another skips the teardown, so a stream can still be playing
    stopStream();
    video.poster = stream.snapshot_url;
    hideLoading();
  } else {
    video.removeAttribute('poster');
    initializeStream(stream.stream_url);
  }
  
  // Setup socket connection
  setupSocket(stream.name);

  // Fetch chat history from REST endpoint
  fetchChatHistory(stream.name);
  
  // Initialize but don't autoplay music
  initializeMusic();
  if (canFeedInCurrentRoom()) {
    startFeedStatusCountdown(stream?.seconds_left_to_feed);
  } else {
    clearFeedStatusCountdown({ reset: false });
  }
 
  const slider = document.getElementById('volumeSlider');

  if (!autoPlayInitialized) {
    autoPlayInitialized = true;
    if (slider) {
      const currentPercent = radioUnlockedByUser ? getCurrentMusicVolumePercent() : 0;
      slider.value = String(currentPercent);
    }
    if (!radioUnlockedByUser) {
      lastSetVolume = 0;
      music.volume = 0;
      music.pause();
    }
    updateMarqueePlayState();
    updateMusicIcons();
  } else {
    if (slider) {
      slider.value = String(getCurrentMusicVolumePercent());
    }
    updateMarqueePlayState();
    updateMusicIcons();
  }

  // Update info modal content with stream description
  updateInfoModalContent(stream);

  // Set room name (and hidden-room lock) in header
  applyLivestreamRoomHeader(stream);

  // Register this room with the admin module so admins can edit it.
  setActiveRoomForAdmin(stream, (updatedStream) => {
    const bannerChanged = updatedStream.banner !== currentStream?.banner
      || updatedStream.banner_visible !== currentStream?.banner_visible;
    currentStream = { ...currentStream, ...updatedStream };
    applyLivestreamRoomHeader(currentStream);
    updateInfoModalContent(currentStream);
    // Left alone otherwise, so a banner the viewer dismissed stays dismissed.
    if (bannerChanged) applyRoomBanner(currentStream);
    applyChatPermissionState();
    if (canFeedInCurrentRoom()) {
      startFeedStatusCountdown(currentStream.seconds_left_to_feed);
    } else {
      clearFeedStatusCountdown({ reset: false });
      updateFeedButtonsDisabled(true, '', FEEDING_DISABLED_MESSAGE);
    }
  });

  // Register this room with the message-admin module so admins can hide chat messages.
  setActiveRoomForMessageAdmin(stream);

  // Setup UI event listeners
  setupUIEventListeners(stream.name);

  bindDonationPopup();
  scheduleDonationPopup();
}

export function teardownLivestreamComponents() {
  document.body.classList.remove('livestream-active');
  const root = document.getElementById('livestream-view');
  if (root) {
    root.classList.remove('is-active');
  }

  clearActiveRoomForAdmin();
  clearActiveRoomForMessageAdmin();
  
  // Hide visibility banner when tearing down
  const visibilityBanner = document.querySelector('.livestream-visibility-banner');
  if (visibilityBanner) {
    visibilityBanner.style.display = 'none';
  }

  if (archivedMessage) archivedMessage.style.display = 'none';

  const lockEl = document.getElementById('roomNameLock');
  if (lockEl) lockEl.hidden = true;
  
  // Clean up chat form submit handler
  const chatForm = document.querySelector('.chat-input-form');
  if (chatForm && chatFormSubmitHandler) {
    chatForm.removeEventListener('submit', chatFormSubmitHandler);
    chatFormSubmitHandler = null;
  }

  // Clean up chat input resize handler
  const chatInput = document.querySelector('.chat-input');
  if (chatInput && chatInputResizeHandler) {
    chatInput.removeEventListener('input', chatInputResizeHandler);
    chatInputResizeHandler = null;
  }
  
  currentStream = null;
  usingSecondaryStream = false;

  // Hide swap camera button
  const swapCameraBtn = document.getElementById('swapCameraButton');
  if (swapCameraBtn) {
    swapCameraBtn.style.display = 'none';
  }

  if (streamReconnectTimeout) {
    clearTimeout(streamReconnectTimeout);
    streamReconnectTimeout = null;
  }

  clearFeedStatusCountdown({ reset: true });
  clearDonationPopupTimer();
  hideDonationPopup();

  // Clean up cooldown interval
  if (cooldownInterval) {
    clearInterval(cooldownInterval);
    cooldownInterval = 0;
  }
  
  const cooldownMessageElement = document.querySelector('.cooldown-message');
  if (cooldownMessageElement) {
    cooldownMessageElement.style.display = 'none';
    cooldownMessageElement.style.opacity = '0';
  }

  // Clean up rate limit interval
  if (rateLimitInterval) {
    clearInterval(rateLimitInterval);
    rateLimitInterval = null;
  }

  // Re-enable chat input if it was disabled
  updateChatInputDisabled(false);

  stopStream();

  lastUnfocusedAt = null;
}

function setupUIEventListeners(roomId) {
  const homeLink = document.querySelector('.home-link');
  if (homeLink && !homeLink.dataset.bound) {
    homeLink.dataset.bound = 'true';
    const navigateHome = () => {
      import('./router.js').then(({ router }) => {
        router.navigate('/');
    });
    };

    homeLink.addEventListener('click', navigateHome);
    homeLink.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        navigateHome();
      }
    });
  }
  bindUserDisplayName();

  // Feed button
  const feed_buttons = document.querySelectorAll('.feed-button');
  feed_buttons.forEach(feed_button => {
    feed_button.dataset.roomId = roomId;
    if (!feed_button.dataset.feedBound) {
      feed_button.dataset.feedBound = 'true';
      feed_button.addEventListener('click', handleFeedButtonClick);
    }
  });

  // Volume slider
  // Chat functionality
  const chatForm = document.querySelector('.chat-input-form');
  const chatInput = document.querySelector('.chat-input');
  const chatMessages = document.querySelector('.chat-messages');

  if (chatForm && chatInput && chatMessages) {
    // Remove old submit handler if it exists
    if (chatFormSubmitHandler) {
      chatForm.removeEventListener('submit', chatFormSubmitHandler);
      chatFormSubmitHandler = null;
    }

    chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        chatForm.requestSubmit();
      }
    });

    // Auto-resize textarea on mobile (but keep single row on mobile)
    chatInputResizeHandler = () => {
      const isMobile = window.innerWidth <= 600;
      chatInput.style.height = 'auto';
      const scrollHeight = chatInput.scrollHeight;
      if (isMobile) {
        // On mobile, keep it single row (44px)
        chatInput.style.height = '44px';
        chatInput.style.overflowY = 'hidden';
      } else {
        const maxHeight = 120; // matches CSS max-height
        chatInput.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
        chatInput.style.overflowY = 'auto';
      }
    };

    chatInput.addEventListener('input', chatInputResizeHandler);
    
    // Handle mobile keyboard visibility
    if ('visualViewport' in window) {
      const handleViewportChange = () => {
        // Scroll chat messages to bottom when keyboard appears
        setTimeout(() => {
          scrollToBottom(chatMessages);
        }, 100);
      };
      
      window.visualViewport.addEventListener('resize', handleViewportChange);
      window.visualViewport.addEventListener('scroll', handleViewportChange);
    }

    // Create new submit handler that uses currentStream.name dynamically
    chatFormSubmitHandler = async (e) => {
      e.preventDefault(); // Prevent page reload

      const message = chatInput.value.trim();
      if (message.length === 0) return;

      if (isChatDisabledByPermission()) return;
      if (currentStream?.message_permission === 'AUTH_USERS' && !isLoggedIn()) {
        openAuthModal({ message: 'log in to send messages in this room' });
        return;
      }

      // Use currentStream.name dynamically instead of captured roomId
      const currentRoomId = currentStream?.name;
      if (!currentRoomId) {
        console.error('Cannot send message: no current stream');
        return;
      }

      // For now, just append the message locally
      const timestampStr = new Date().toLocaleTimeString(DATE_LOCALE, {hour: '2-digit', minute: '2-digit'});
      const msgDiv = document.createElement('div');
      msgDiv.className = 'chat-message';
      
      const timestamp = document.createElement('span');
      timestamp.className = 'chat-timestamp';
      timestamp.textContent = timestampStr;
      
      const self = getBackendUser();
      const displayName = (self && self.display_name) || userName;
      const avatarUrl = self && self.avatar_url;

      const selfRoles = self ? getRoomRoles(self.room_admin_users, currentStream?.name) : null;
      const selfIsOwner = selfRoles?.includes('ROOM_OWNER') ?? false;

      const username = document.createElement('span');
      username.className = 'chat-username';
      username.textContent = displayName;
      if (self && self.id) {
        username.classList.add('chat-username--registered');
        username.addEventListener('click', () => openProfileModal());
      }

      const content = document.createElement('span');
      content.className = 'chat-content';
      content.textContent = message;

      msgDiv.appendChild(timestamp);
      if (self) {
        if (selfIsOwner) {
          msgDiv.appendChild(createChatAdminCrown());
        }
        const avatar = document.createElement('span');
        avatar.className = 'chat-avatar';
        if (avatarUrl) {
          const img = document.createElement('img');
          img.className = 'chat-avatar-img';
          img.alt = '';
          img.src = avatarUrl;
          const placeholder = document.createElement('img');
          placeholder.src = '/icons/user.svg';
          placeholder.alt = '';
          placeholder.className = 'chat-avatar-placeholder';
          placeholder.style.display = 'none';
          img.onerror = () => { img.style.display = 'none'; placeholder.style.display = ''; };
          avatar.append(img, placeholder);
        } else {
          const placeholder = document.createElement('img');
          placeholder.src = '/icons/user.svg';
          placeholder.alt = '';
          placeholder.className = 'chat-avatar-placeholder';
          avatar.appendChild(placeholder);
        }
        msgDiv.appendChild(avatar);
      }
      msgDiv.appendChild(username);
      msgDiv.appendChild(content);
      chatMessages.appendChild(msgDiv);

      chatInput.value = '';
      // Reset textarea height
      chatInput.style.height = 'auto';
      scrollToBottom(chatMessages);
      // Refocus input on mobile
      setTimeout(() => {
        chatInput.focus();
      }, 50);

      const token = await getToken();
      const messageData = {
        'content': message,
        'user': userName,
        'deviceId': deviceId,
        'timestamp': new Date().toISOString(),
        'roomId': currentRoomId,
        'token': token || null
      }

      socket.emit('send_message', messageData);
    };

    chatForm.addEventListener('submit', chatFormSubmitHandler);
  }

  // Initial icon update
  updateMusicIcons();

  // Gallery button navigation
  const galleryButtons = document.querySelectorAll('.gallery-button, .gallery-button-secondary');
  galleryButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      if (currentStream?.name) {
        import('./router.js').then(({ router }) => {
          router.navigate(`/${currentStream.name}/gallery`);
        });
      }
    });
  });

  // Gallery tip banner. Archived rooms always show it, as the gallery is all that's left of them
  const galleryTip = document.getElementById('galleryTip');
  const showGalleryTip = currentStream?.archived || !localStorage.getItem('galleryTipDismissed');
  if (galleryTip) galleryTip.style.display = showGalleryTip ? '' : 'none';
  if (galleryTip && showGalleryTip) {
    const closeTip = galleryTip.querySelector('.gallery-tip__close');
    if (closeTip) {
      closeTip.addEventListener('click', () => {
        galleryTip.style.display = 'none';
        localStorage.setItem('galleryTipDismissed', '1');
      });
    }
    galleryButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        galleryTip.style.display = 'none';
        localStorage.setItem('galleryTipDismissed', '1');
      });
    });
  }

  // Info Modal Logic
  const infoButton = document.querySelector('.project-info-container');
  const infoModal = document.getElementById('infoModal');
  const infoModalContent = infoModal ? infoModal.querySelector('.info-modal-content') : null;
  const closeModalBtn = document.getElementById('closeModal');

  // Tabs Logic (scoped to info modal only)
  const modalTabs = infoModal ? infoModal.querySelectorAll('.modal-tab') : [];
  const tabContents = infoModal ? infoModal.querySelectorAll('.tab-content') : [];

  async function loadLeaderboard() {
    if (leaderboardLoaded) return;
    const roomName = currentStream?.name;
    if (!roomName) return;

    const list = document.getElementById('roomLeaderboardList');
    const loading = document.getElementById('roomLeaderboardLoading');
    const empty = document.getElementById('roomLeaderboardEmpty');
    if (!list) return;

    if (loading) loading.style.display = 'flex';
    list.style.display = 'none';
    if (empty) empty.style.display = 'none';

    try {
      const res = await fetch(`${apiUrl}/rooms/${roomName}/top-feeders?limit=10`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      const users = data.users || [];

      if (loading) loading.style.display = 'none';

      if (users.length === 0) {
        if (empty) empty.style.display = '';
        return;
      }

      list.innerHTML = '';
      users.forEach((user, i) => {
        const row = document.createElement('div');
        row.className = 'leaderboard-row';

        const rank = document.createElement('span');
        rank.className = 'leaderboard-rank';
        rank.textContent = `${i + 1}`;

        const avatar = document.createElement('div');
        avatar.className = 'leaderboard-avatar';
        if (user.avatar_url) {
          const img = document.createElement('img');
          img.src = user.avatar_url;
          img.alt = '';
          img.className = 'leaderboard-avatar-img';
          img.onerror = () => { img.style.display = 'none'; placeholder.style.display = ''; };
          const placeholder = document.createElement('img');
          placeholder.src = '/icons/user.svg';
          placeholder.alt = '';
          placeholder.className = 'leaderboard-avatar-placeholder';
          placeholder.style.display = 'none';
          avatar.append(img, placeholder);
        } else {
          const placeholder = document.createElement('img');
          placeholder.src = '/icons/user.svg';
          placeholder.alt = '';
          placeholder.className = 'leaderboard-avatar-placeholder';
          avatar.appendChild(placeholder);
        }

        const self = getBackendUser();
        const isMe = self && self.id === user.id;

        if (isMe) row.classList.add('leaderboard-row--me');

        const name = document.createElement('span');
        name.className = 'leaderboard-name';
        name.textContent = user.display_name || 'user';
        name.addEventListener('click', () => {
          if (isMe) openProfileModal();
          else openUserCard(user.id);
        });

        const count = document.createElement('span');
        count.className = 'leaderboard-count';
        const n = user.times_fed_in_room;
        count.textContent = `${n} feed${n !== 1 ? 's' : ''}`;

        row.append(rank, avatar, name, count);
        list.appendChild(row);
      });

      list.style.display = '';
      leaderboardLoaded = true;
    } catch (_) {
      if (loading) loading.style.display = 'none';
      if (empty) { empty.textContent = 'failed to load leaderboard'; empty.style.display = ''; }
    }
  }

  function switchTab(targetId) {
    modalTabs.forEach(tab => tab.classList.remove('active'));
    tabContents.forEach(content => {
        content.classList.remove('active');
        content.style.display = 'none';
    });

    const targetTab = infoModal ? infoModal.querySelector(`.modal-tab[data-target="${targetId}"]`) : null;
    const targetContent = infoModal ? infoModal.querySelector(`#${targetId}`) : null;

    if (targetTab && targetContent) {
        targetTab.classList.add('active');
        targetContent.classList.add('active');
        targetContent.style.display = 'flex';
    }

    if (targetId === 'statsTabContent') {
      loadLeaderboard();
    }
  }

  modalTabs.forEach(tab => {
    tab.addEventListener('click', () => {
        const targetId = tab.dataset.target;
        switchTab(targetId);
    });
  });

  function openInfoModal() {
    if (!infoModal || !infoModalContent) return;
    if (infoModal.classList.contains('active')) return;
    infoModal.classList.add('active');
    modalOpen();
    infoModalContent.style.display = 'flex';
    
    // Reset to first tab when opening
    switchTab('infoTabContent');
  }

  function closeInfoModal() {
    if (!infoModal || !infoModalContent) return;
    if (!infoModal.classList.contains('active')) return;
    infoModal.classList.remove('active');
    modalClose();
    infoModalContent.style.display = 'none';
  }

  if (infoButton) {
    infoButton.addEventListener('click', openInfoModal);
  }
  if (closeModalBtn) {
    closeModalBtn.addEventListener('click', closeInfoModal);
  }
  // Close modal when clicking outside the content
  if (infoModal) {
    infoModal.addEventListener('click', function(e) {
      if (e.target === infoModal) {
        closeInfoModal();
      }
    });
  }
  // Optional: Close modal with Escape key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && infoModal.classList.contains('active')) {
      closeInfoModal();
    }
  });

  // Re-evaluate marquee on resize (mobile rotations etc.)
  window.addEventListener('resize', () => {
    const feedButtons = document.querySelectorAll('.feed-button');
    feedButtons.forEach(btn => fitButtonText(btn));

    const container = document.querySelector('.song-info');
    if (!container) return;
    const innerOne = container.querySelector('.marquee__inner');
    const track = container.querySelector('.marquee__track');
    const marquee = container.querySelector('.marquee');
    if (!innerOne || !track || !marquee) return;
    const textWidth = innerOne.getBoundingClientRect().width;
    const containerWidth = container.getBoundingClientRect().width;
    const forceMarquee = window.innerWidth <= 900;
    if (forceMarquee || textWidth > containerWidth) {
      marquee.classList.remove('marquee--static');
      container.classList.add('is-overflowing');
      const pxPerSecond = 40;
      const duration = Math.max(8, (textWidth * 2) / pxPerSecond);
      container.style.setProperty('--marquee-duration', `${duration}s`);
      container.style.setProperty('--marquee-translate', `-${textWidth}px`);
      track.style.width = `${textWidth * 2}px`;
    } else {
      marquee.classList.add('marquee--static');
      container.classList.remove('is-overflowing');
      container.style.removeProperty('--marquee-duration');
      container.style.removeProperty('--marquee-translate');
      track.style.removeProperty('width');
    }
  });
}
